// admin.onlytrade.ai/cert-groups — admin curates the placards shown on the
// engineer-facing /certificates picker. Each group has a label + icon and a
// list of cert_type members. Hits /api/admin/cert-type-groups (mounted in
// server.js, gated by requireAdminRole).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { certGroups as api, type CertGroup, type CertCatalogueItem } from '../api';
import { useToast } from '../lib/toast';

export default function CertGroupsPage() {
  const { push: toast } = useToast();
  const [groups, setGroups] = useState<CertGroup[] | null>(null);
  const [catalogue, setCatalogue] = useState<CertCatalogueItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CertGroup | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.list();
      setGroups(d.groups);
      setCatalogue(d.catalogue);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function toggleEnabled(g: CertGroup) {
    try {
      await api.update(g.id, { is_enabled: !g.is_enabled });
      toast(`${g.label} ${!g.is_enabled ? 'enabled' : 'disabled'}`);
      void load();
    } catch (e) { toast(`Failed: ${(e as Error).message}`, 'error'); }
  }

  async function remove(g: CertGroup) {
    if (!confirm(`Delete the "${g.label}" group? Cert types will not be deleted, just removed from this group.`)) return;
    try { await api.remove(g.id); toast(`Deleted ${g.label}`); void load(); }
    catch (e) { toast(`Failed: ${(e as Error).message}`, 'error'); }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-bold mb-1">Cert Picker Groups</h1>
          <p className="text-ink-muted text-sm">
            Top-level groupings shown on <code>onlytrade.ai/certificates</code> when engineers tap <strong>+ New Certificate</strong>.
            Drag-rearrange members to control display order. Empty groups are hidden from the picker.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)}
                className="shrink-0 whitespace-nowrap bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded font-semibold">
          + New Group
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700 text-red-200 p-3 rounded mb-4">{error}</div>}

      {groups === null ? (
        <div className="text-ink-muted">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="bg-ink-card border border-ink-border rounded-lg p-8 text-center">
          <div className="text-3xl mb-2">📁</div>
          <div className="font-semibold mb-1">No groups yet</div>
          <div className="text-ink-muted text-sm mb-4">The picker is using its hardcoded category fallback (Gas Safety, Renewables, …). Create groups to override.</div>
          <button onClick={() => setShowCreate(true)} className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded font-semibold">
            Create your first group
          </button>
        </div>
      ) : (
        <div className="grid gap-3">
          {groups.map(g => (
            <div key={g.id} className={`bg-ink-card border ${g.is_enabled ? 'border-ink-border' : 'border-ink-border opacity-50'} rounded-lg p-4`}>
              <div className="flex items-center gap-3">
                <div className="text-2xl">{g.icon || '📁'}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {g.label}
                    {!g.is_enabled && <span className="text-xs bg-ink-bg border border-ink-border px-2 py-0.5 rounded">Disabled</span>}
                    <span className="text-xs text-ink-muted">order {g.display_order}</span>
                  </div>
                  <div className="text-xs text-ink-muted mt-1 truncate">
                    {g.members.length === 0 ? 'No forms assigned' : `${g.members.length} form${g.members.length === 1 ? '' : 's'}: ${g.members.slice(0, 6).map(m => labelFor(m, catalogue)).join(', ')}${g.members.length > 6 ? '…' : ''}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(g)} className="bg-ink-hover hover:bg-ink-border px-3 py-1.5 rounded text-sm font-semibold">Edit</button>
                  <button onClick={() => toggleEnabled(g)} className="bg-ink-hover hover:bg-ink-border px-3 py-1.5 rounded text-sm">{g.is_enabled ? 'Disable' : 'Enable'}</button>
                  <button onClick={() => remove(g)} className="bg-ink-hover hover:bg-red-900/40 hover:text-red-300 px-3 py-1.5 rounded text-sm">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && <CreateGroupModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); void load(); }} toast={toast} />}
      {editing && <EditGroupModal group={editing} catalogue={catalogue} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} toast={toast} />}
    </div>
  );
}

function labelFor(certType: string, catalogue: CertCatalogueItem[]): string {
  const c = catalogue.find(x => x.cert_type === certType);
  return c ? c.label : certType;
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

function CreateGroupModal({ onClose, onCreated, toast }: { onClose: () => void; onCreated: () => void; toast: (m: string, k?: 'success' | 'error') => void }) {
  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState('');
  const [display_order, setOrder] = useState(100);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!label.trim()) return;
    setBusy(true);
    try {
      const slug = slugify(label.trim());
      await api.create({ slug, label: label.trim(), icon: icon.trim() || null, display_order });
      toast(`Created "${label}"`);
      onCreated();
    } catch (e) { toast(`Failed: ${(e as Error).message}`, 'error'); setBusy(false); }
  }

  return (
    <Modal onClose={onClose} title="New cert picker group">
      <Field label="Label">
        <input autoFocus value={label} onChange={e => setLabel(e.target.value)}
               placeholder="Air Conditioning"
               className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        <div className="text-xs text-ink-muted mt-1">Slug will be <code>{label.trim() ? slugify(label.trim()) : '—'}</code></div>
      </Field>
      <Field label="Icon (emoji)">
        <input value={icon} onChange={e => setIcon(e.target.value)}
               placeholder="❄️"
               className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
      </Field>
      <Field label="Display order">
        <input type="number" value={display_order} onChange={e => setOrder(parseInt(e.target.value, 10) || 100)}
               className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        <div className="text-xs text-ink-muted mt-1">Lower numbers show first.</div>
      </Field>
      <div className="flex gap-2 justify-end pt-2">
        <button onClick={onClose} className="px-4 py-2 rounded bg-ink-hover">Cancel</button>
        <button onClick={submit} disabled={busy || !label.trim()}
                className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-700 text-white font-semibold disabled:opacity-50">
          {busy ? 'Creating…' : 'Create group'}
        </button>
      </div>
    </Modal>
  );
}

function EditGroupModal({ group, catalogue, onClose, onSaved, toast }: { group: CertGroup; catalogue: CertCatalogueItem[]; onClose: () => void; onSaved: () => void; toast: (m: string, k?: 'success' | 'error') => void }) {
  const [label, setLabel] = useState(group.label);
  const [icon, setIcon] = useState(group.icon || '');
  const [order, setOrder] = useState(group.display_order);
  const [members, setMembers] = useState<string[]>([...group.members]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');

  const inGroup = useMemo(() => new Set(members), [members]);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return catalogue.filter(c => !q || c.label.toLowerCase().includes(q) || c.cert_type.toLowerCase().includes(q));
  }, [catalogue, filter]);

  function toggle(certType: string) {
    setMembers(prev => prev.includes(certType) ? prev.filter(t => t !== certType) : [...prev, certType]);
  }
  function move(certType: string, dir: -1 | 1) {
    setMembers(prev => {
      const i = prev.indexOf(certType);
      if (i < 0) return prev;
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    setBusy(true);
    try {
      const labelChanged = label !== group.label;
      const iconChanged = (icon || null) !== (group.icon || null);
      const orderChanged = order !== group.display_order;
      if (labelChanged || iconChanged || orderChanged) {
        await api.update(group.id, {
          ...(labelChanged ? { label } : {}),
          ...(iconChanged ? { icon: icon || null } : {}),
          ...(orderChanged ? { display_order: order } : {}),
        });
      }
      const sameMembers = members.length === group.members.length && members.every((m, i) => group.members[i] === m);
      if (!sameMembers) await api.setMembers(group.id, members);
      toast(`Saved "${label}"`);
      onSaved();
    } catch (e) { toast(`Failed: ${(e as Error).message}`, 'error'); setBusy(false); }
  }

  return (
    <Modal onClose={onClose} title={`Edit ${group.label}`} wide>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Label">
          <input value={label} onChange={e => setLabel(e.target.value)}
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        </Field>
        <Field label="Icon (emoji)">
          <input value={icon} onChange={e => setIcon(e.target.value)}
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        </Field>
        <Field label="Display order">
          <input type="number" value={order} onChange={e => setOrder(parseInt(e.target.value, 10) || 100)}
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="font-semibold mb-2 text-sm">Members ({members.length}) — order shown</div>
          <div className="border border-ink-border rounded max-h-72 overflow-y-auto">
            {members.length === 0
              ? <div className="p-4 text-sm text-ink-muted text-center">No forms in this group yet</div>
              : members.map((t, i) => {
                const c = catalogue.find(x => x.cert_type === t);
                return (
                  <div key={t} className="flex items-center gap-2 px-3 py-2 border-b border-ink-border last:border-0">
                    <span className="text-lg">{c?.icon || '📄'}</span>
                    <span className="flex-1 text-sm truncate">{c?.label || t}</span>
                    <button onClick={() => move(t, -1)} disabled={i === 0} className="p-1 text-ink-muted hover:text-ink-text disabled:opacity-30" aria-label="Move up">↑</button>
                    <button onClick={() => move(t, 1)} disabled={i === members.length - 1} className="p-1 text-ink-muted hover:text-ink-text disabled:opacity-30" aria-label="Move down">↓</button>
                    <button onClick={() => toggle(t)} className="p-1 text-ink-muted hover:text-red-400" aria-label="Remove">×</button>
                  </div>
                );
              })}
          </div>
        </div>

        <div>
          <div className="font-semibold mb-2 text-sm">All cert types — tap to add/remove</div>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by name or key…"
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2 mb-2 text-sm" />
          <div className="border border-ink-border rounded max-h-60 overflow-y-auto">
            {filtered.map(c => {
              const inG = inGroup.has(c.cert_type);
              return (
                <button key={c.cert_type} onClick={() => toggle(c.cert_type)}
                        className={`w-full flex items-center gap-2 px-3 py-2 border-b border-ink-border last:border-0 text-left text-sm ${inG ? 'bg-brand-600/15' : 'hover:bg-ink-hover'}`}>
                  <span className="text-lg">{c.icon || '📄'}</span>
                  <span className="flex-1 truncate">{c.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${inG ? 'bg-brand-600 text-white' : 'bg-ink-bg text-ink-muted'}`}>{inG ? 'In group' : '+ Add'}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <button onClick={onClose} className="px-4 py-2 rounded bg-ink-hover">Cancel</button>
        <button onClick={save} disabled={busy}
                className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-700 text-white font-semibold disabled:opacity-50">
          {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block mb-3">
      <span className="block text-sm font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}

function Modal({ children, onClose, title, wide }: { children: React.ReactNode; onClose: () => void; title: string; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className={`bg-ink-card border border-ink-border rounded-lg p-5 w-full ${wide ? 'max-w-3xl' : 'max-w-md'} mt-12`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-text text-2xl leading-none">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}
